import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { OutlinedInput } from '@mui/material';
import * as Yup from 'yup';

import {
  CONSTANT_KEYS,
  RELATIONS_KEYS,
  REPLACEMENTS_KEYS,
} from '../../helpers/tableKeys';
import {
  REPLACED_PATTERN,
  REPLACEMENT_PATTERN,
} from '../../helpers/validationPatterns';
import PATH from '../../helpers/routes.json';

const { TEETH, NAME_RU, NAME_EN, CODE } = CONSTANT_KEYS;
const { REPLACED, REPLACEMENT } = REPLACEMENTS_KEYS;
const { COEFFICIENT } = RELATIONS_KEYS;

const EditableCell = ({
  cell,
  onChangeData,
  value: initialValue,
  pathname,
}) => {
  const {
    row: { index, original },
    column: { id: columnName },
  } = cell;

  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState('');

  const { t } = useTranslation(['promptbar']);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const schema = Yup.object().shape({
    [NAME_RU]: Yup.string().matches(/[a-zA-Zа-яА-Я]+/, t('incorrectFormat')),
    [NAME_EN]:
      pathname === PATH.services.primary
        ? Yup.string().matches(/[a-zA-Z]+/, t('incorrectFormat'))
        : Yup.string(),
    [TEETH]: Yup.string().matches(/^[\d,\s-]*$/, t('incorrectFormat')),
    [REPLACED]: Yup.string().matches(REPLACED_PATTERN, t('incorrectFormat')),
    [REPLACEMENT]: Yup.string().matches(
      REPLACEMENT_PATTERN,
      t('incorrectFormat'),
    ),
    [COEFFICIENT]: Yup.string()
      .required(t('promptbar:requiredField'))
      .matches(/^\d+$/, t('incorrectFormat')),
    [CODE]: pathname.includes('keywords')
      ? Yup.string().trim().required(t('promptbar:requiredField'))
      : Yup.string(),
  });

  const onChange = e => {
    setError('');
    setValue(e.target.value);
  };

  const handleValidate = async () => {
    const fieldSchema = schema.fields[columnName];
    if (!fieldSchema) return;

    try {
      await fieldSchema.validate(value, { abortEarly: false });
      setError('');
    } catch (errors) {
      let validationError = '';
      errors.inner?.forEach(error => {
        validationError = error.message;
      });
      setError(validationError);
    }
  };

  const onBlur = async () => {
    const fieldSchema = schema.fields[columnName];
    let isFormValid = true;

    if (fieldSchema) {
      isFormValid = await fieldSchema.isValid(value);
    }

    handleValidate();

    let newValue = value;
    if (columnName === NAME_RU || columnName === NAME_EN) {
      newValue = value.charAt(0).toUpperCase() + value.slice(1);
    }

    onChangeData({
      index: original.uniqId,
      value: newValue.trim(),
      columnName,
      isValid: isFormValid,
    });
  };

  return (
    <OutlinedInput
      id={columnName + '-' + index}
      name={columnName}
      multiline
      fullWidth
      maxRows={3}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      sx={{ fontSize: '0.875rem', lineHeight: 1.43 }}
      error={Boolean(error)}
    />
  );
};

EditableCell.propTypes = {
  cell: PropTypes.object.isRequired,
  onChangeData: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pathname: PropTypes.string,
};

export default EditableCell;
